import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type {
  JobWorkIssueRegisterRow,
  JobWorkOrderRegisterRow,
  JobWorkPendingAtVendorRow,
  JobWorkReceiptRegisterRow,
} from '@/types/reports';
import { withId } from './reportHelpers';

const TABS = ['jobWorkOrderRegister', 'jobWorkPendingAtVendor', 'jobWorkIssueRegister', 'jobWorkReceiptRegister'] as const;

/** Job Work registers. The nav leaf pointing here is gated on ProcessConfiguration.JobWorkEnabled,
 * so this page is only reachable while the module is on — mirroring Client Summary's own
 * thirdPartyLogisticsEnabled gate, the other config-gated report in this build. */
export default function JobWorkReportsPage() {
  const { t } = useTranslation('reports');
  const { formatDate } = useDateFormat();
  const { data: processConfiguration } = useProcessConfiguration();
  const showClientColumn = Boolean(processConfiguration?.thirdPartyLogisticsEnabled);
  const [tab, setTab] = useState(0);

  const { data: orders = [], isLoading: loadingOrders } = useQuery({
    queryKey: ['reports', 'job-work-order-register'],
    queryFn: async () => (await apiClient.get<JobWorkOrderRegisterRow[]>('/reports/job-work-order-register')).data,
    enabled: tab === 0,
  });
  const { data: pending = [], isLoading: loadingPending } = useQuery({
    queryKey: ['reports', 'job-work-pending-at-vendor'],
    queryFn: async () => (await apiClient.get<JobWorkPendingAtVendorRow[]>('/reports/job-work-pending-at-vendor')).data,
    enabled: tab === 1,
  });
  const { data: issues = [], isLoading: loadingIssues } = useQuery({
    queryKey: ['reports', 'job-work-issue-register'],
    queryFn: async () => (await apiClient.get<JobWorkIssueRegisterRow[]>('/reports/job-work-issue-register')).data,
    enabled: tab === 2,
  });
  const { data: receipts = [], isLoading: loadingReceipts } = useQuery({
    queryKey: ['reports', 'job-work-receipt-register'],
    queryFn: async () => (await apiClient.get<JobWorkReceiptRegisterRow[]>('/reports/job-work-receipt-register')).data,
    enabled: tab === 3,
  });

  const orderColumns: DataTableColumn<ReturnType<typeof withId<JobWorkOrderRegisterRow>>[number]>[] = [
    { field: 'orderNumber', headerName: t('columns.number') },
    { field: 'vendorName', headerName: t('columns.vendor'), filterable: true },
    { field: 'outputMaterialCode', headerName: t('columns.outputMaterial'), render: (r) => `${r.outputMaterialCode} — ${r.outputMaterialName}` },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'orderDate', headerName: t('columns.date'), render: (r) => formatDate(r.orderDate) },
    { field: 'expectedReturnDate', headerName: t('columns.expectedReturn'), render: (r) => (r.expectedReturnDate ? formatDate(r.expectedReturnDate) : '—') },
    { field: 'plannedQty', headerName: t('columns.plannedQty'), align: 'right' },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.isClosed ? 'Closed' : r.status} />, filterable: true },
    ...(showClientColumn
      ? [{ field: 'clientName', headerName: t('columns.client'), render: (r: JobWorkOrderRegisterRow) => r.clientName ?? '—', filterable: true } as DataTableColumn<ReturnType<typeof withId<JobWorkOrderRegisterRow>>[number]>]
      : []),
  ];

  const pendingColumns: DataTableColumn<ReturnType<typeof withId<JobWorkPendingAtVendorRow>>[number]>[] = [
    { field: 'orderNumber', headerName: t('columns.number') },
    { field: 'vendorName', headerName: t('columns.vendor'), filterable: true },
    { field: 'outputMaterialCode', headerName: t('columns.outputMaterial') },
    { field: 'issuedQty', headerName: t('columns.sentQty'), align: 'right' },
    { field: 'pendingToSendQty', headerName: t('columns.stillToSend'), align: 'right' },
    { field: 'receivedQty', headerName: t('columns.receivedQty'), align: 'right' },
    { field: 'acceptedQty', headerName: t('columns.acceptedQty'), align: 'right' },
    { field: 'rejectedQty', headerName: t('columns.rejectedQty'), align: 'right' },
    { field: 'pendingToReceiveQty', headerName: t('columns.stillAtVendor'), align: 'right' },
    {
      field: 'ageInDays', headerName: t('columns.age'), align: 'right',
      // The reason this register exists — an order sitting at a vendor past its expected return
      // date, with material genuinely still out, is what the warehouse chases.
      render: (r) => (r.isOverdue
        ? <Chip size="small" color="error" label={t('columns.overdueDays', { days: r.ageInDays })} />
        : t('columns.days', { days: r.ageInDays })),
    },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.isClosed ? 'Closed' : r.status} />, filterable: true },
  ];

  const issueColumns: DataTableColumn<ReturnType<typeof withId<JobWorkIssueRegisterRow>>[number]>[] = [
    { field: 'issueNumber', headerName: t('columns.number') },
    { field: 'jobWorkOrderNumber', headerName: t('columns.jobWorkOrder'), filterable: true },
    { field: 'vendorName', headerName: t('columns.vendor'), filterable: true },
    { field: 'issueDate', headerName: t('columns.date'), render: (r) => formatDate(r.issueDate) },
    { field: 'outwardDcNo', headerName: t('columns.outwardDc'), render: (r) => r.outwardDcNo ?? '—' },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'qty', headerName: t('columns.qty'), align: 'right' },
    { field: 'locationCode', headerName: t('columns.location'), render: (r) => r.locationCode ?? '—' },
    { field: 'batchNo', headerName: t('columns.batchNo'), render: (r) => r.batchNo ?? '—' },
  ];

  const receiptColumns: DataTableColumn<ReturnType<typeof withId<JobWorkReceiptRegisterRow>>[number]>[] = [
    { field: 'receiptNumber', headerName: t('columns.number') },
    { field: 'jobWorkOrderNumber', headerName: t('columns.jobWorkOrder'), filterable: true },
    { field: 'vendorName', headerName: t('columns.vendor'), filterable: true },
    { field: 'receiptDate', headerName: t('columns.date'), render: (r) => formatDate(r.receiptDate) },
    { field: 'vendorDcNo', headerName: t('columns.vendorDc'), render: (r) => r.vendorDcNo ?? '—' },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'receivedQty', headerName: t('columns.receivedQty'), align: 'right' },
    { field: 'acceptedQty', headerName: t('columns.acceptedQty'), align: 'right' },
    { field: 'rejectedQty', headerName: t('columns.rejectedQty'), align: 'right' },
    { field: 'batchNo', headerName: t('columns.batchNo'), render: (r) => r.batchNo ?? '—' },
    { field: 'qcStatus', headerName: t('columns.qcStatus'), render: (r) => (r.qcStatus ? <StatusChip status={r.qcStatus} /> : '—') },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
  ];

  return (
    <>
      <PageHeader title={t('modules.jobWork.title')} subtitle={t('modules.jobWork.subtitle')} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {TABS.map((key) => (
          <Tab key={key} label={t(`${key}.title`)} />
        ))}
      </Tabs>

      <Box hidden={tab !== 0}>
        <DataTable columns={orderColumns} rows={withId(orders, (r) => r.jobWorkOrderId)} loading={loadingOrders} searchable exportFileName="job-work-order-register" />
      </Box>
      <Box hidden={tab !== 1}>
        <DataTable columns={pendingColumns} rows={withId(pending, (r) => r.jobWorkOrderId)} loading={loadingPending} searchable exportFileName="job-work-pending-at-vendor" />
      </Box>
      {/* Composite ids — these two registers are line-wise (one row per document line), so the
          document id alone repeats across rows. Same shape as the Production receipt register. */}
      <Box hidden={tab !== 2}>
        <DataTable columns={issueColumns} rows={withId(issues, (r) => `${r.issueId}-${r.materialCode}`)} loading={loadingIssues} searchable exportFileName="job-work-issue-register" />
      </Box>
      <Box hidden={tab !== 3}>
        <DataTable columns={receiptColumns} rows={withId(receipts, (r) => `${r.receiptId}-${r.materialCode}`)} loading={loadingReceipts} searchable exportFileName="job-work-receipt-register" />
      </Box>
    </>
  );
}
